import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { useLocation } from "react-router-dom";

import AppLayout from "./components/AppLayout";
import { gameFormatPollQuery } from "./components/GameFormatPoll";
import { PollRequiredPage } from "./components/PollRequiredPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AdminPlayersPage from "./pages/AdminPlayersPageV3";
import AdminsPage from "./pages/AdminsPage";
import AuthPage from "./pages/AuthPageV2";
import HistoryPage from "./pages/HistoryPage";
import HomePage from "./pages/HomeArenaPageV2";
import ImportDataPage from "./pages/ImportDataPage";
import NewMatchPage from "./pages/NewMatchPage";
import PlayersPage from "./pages/PlayersPageV2";
import PlayerDetailPage from "./pages/PlayerDetailPage";
import ProfilePage from "./pages/ProfilePageV2";
import ProfileSetupPage from "./pages/ProfileSetupPage";
import PasswordRecoveryPage from "./pages/PasswordRecoveryPage";
import RatePlayersPage from "./pages/RatePlayersPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TeamsPage from "./pages/TeamsPageV4";
import TreasuryPage from "./pages/TreasuryPage";
import "./App.css";
import "./ModernTheme.css";

function Protected({ children, adminOnly = false }) {
  const { user, checking } = useAuth();
  if (checking) return <main className="app-loader" data-testid="app-loading">Preparando o jogo...</main>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.profile_completed && window.location.pathname !== "/completar-perfil") return <Navigate to="/completar-perfil" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function PollGate({ children }) {
  const location = useLocation();
  const pollQuery = useQuery({
    queryKey: ["game-format-poll"],
    queryFn: gameFormatPollQuery,
  });

  if (pollQuery.isLoading) {
    return <main className="app-loader" data-testid="poll-access-loading">Carregando enquete...</main>;
  }
  if (pollQuery.isError) {
    return (
      <main className="app-loader" data-testid="poll-access-error">
        <p>Não foi possível carregar a enquete.</p>
        <button onClick={() => pollQuery.refetch()} data-testid="retry-poll-access-button">
          Tentar novamente
        </button>
      </main>
    );
  }
  if (!pollQuery.data?.has_responded) {
    if (location.pathname !== "/") {
      return <Navigate to="/" replace />;
    }
    return <PollRequiredPage poll={pollQuery.data} />;
  }
  return children;
}

function RoutedApp() {
  return (
    <Routes>
      <Route path="/login" element={<AuthPage />} />
      <Route path="/esqueci-senha" element={<PasswordRecoveryPage />} />
      <Route path="/resetar-senha" element={<ResetPasswordPage />} />
      <Route
        path="/completar-perfil"
        element={<Protected><ProfileSetupPage /></Protected>}
      />
      <Route element={<Protected><PollGate><AppLayout /></PollGate></Protected>}>
        <Route path="/" element={<HomePage />} />
        <Route path="/jogadores" element={<PlayersPage />} />
        <Route path="/jogadores/:playerId" element={<PlayerDetailPage />} />
        <Route path="/avaliar" element={<RatePlayersPage />} />
        <Route path="/times" element={<TeamsPage />} />
        <Route path="/caixa" element={<TreasuryPage />} />
        <Route path="/perfil" element={<ProfilePage />} />
        <Route path="/historico" element={<HistoryPage />} />
        <Route path="/nova-pelada" element={<Protected adminOnly><NewMatchPage /></Protected>} />
        <Route path="/admin/jogadores" element={<Protected adminOnly><AdminPlayersPage /></Protected>} />
        <Route path="/admin/administradores" element={<Protected adminOnly><AdminsPage /></Protected>} />
        <Route path="/admin/importar-dados" element={<Protected adminOnly><ImportDataPage /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RoutedApp />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}
